// ============================================================================
//  Project metadata — single source of truth for the 2D portfolio page.
//  Each project reuses the SAME translation keys and dialog components as the
//  3D portfolio, so clicking a card opens the existing project dialog.
// ============================================================================

import type { ComponentType } from 'react';

import deckwertErmittler from '../../utilities/images/deckwertErmittler.png';
import seasonAppIcon from '../../utilities/images/seasonAppIcon.jpg';
import dataPredictionModel from '../../utilities/images/dataPredictionModel.png';
import websiteRemake from '../../utilities/images/websiteRemake.png';
import workTimeTrackerImage1 from '../../utilities/images/WorkTimeTracker/WorkTimeTrackerDialog1.jpg';
import zenRadarImage1 from '../../utilities/images/ZenRadar/ZenRadarDialog1.png';
import kitchenSyncImage from '../../utilities/images/KitchenSync/kitchenSync.png';
import coupleQuestsImage from '../../utilities/images/CoupleQuests/coupleQuests.png';
import billKeeperImage from '../../utilities/images/billKeeper/billKeeper.png';

import PriceCalculatorDialog from '../dialogs/Projects/priceCalculatorDialog';
import SeasonAppDialog from '../dialogs/Projects/seasonAppDialog';
import DataPredictionDialog from '../dialogs/Projects/dataPredictionDialog';
import WebsiteRemakeDialog from '../dialogs/Projects/websiteRemakeDialog';
import WorkTimeTrackerDialog from '../dialogs/Projects/WorkTimeTrackerDialog';
import ZenRadarDialog from '../dialogs/Projects/ZenRadarDialog';
import KitchenSyncDialog from '../dialogs/Projects/KitchenSyncDialog';
import CoupleQuestsDialog from '../dialogs/Projects/CoupleQuestsDialog';
import BillKeeperDialog from '../dialogs/Projects/BillKeeperDialog';

export interface ProjectDialogProps {
  open: boolean;
  onClose: () => void;
}

export interface ProjectMeta {
  id: string;
  titleKey: string;
  descriptionKey: string;
  thumbnail: string;
  tech: string[];
  featureKeys?: string[];
  githubUrl?: string;
  websiteUrl?: string;
  googlePlayUrl?: string;
  DialogComponent: ComponentType<ProjectDialogProps>;
}

export const projectsData: ProjectMeta[] = [
  {
    id: 'priceCalculator',
    titleKey: 'projectDialogs.priceCalculator.title',
    descriptionKey: 'projectDialogs.priceCalculator.description',
    thumbnail: deckwertErmittler,
    tech: ['React', 'TypeScript', 'CSS', 'Material UI'],
    githubUrl: 'https://github.com/SirArchy/DeckwertErmittler',
    DialogComponent: PriceCalculatorDialog,
  },
  {
    id: 'seasonApp',
    titleKey: 'projectDialogs.seasonApp.title',
    descriptionKey: 'projectDialogs.seasonApp.description',
    thumbnail: seasonAppIcon,
    tech: ['Vue 3', 'Firebase', 'TypeScript'],
    DialogComponent: SeasonAppDialog,
  },
  {
    id: 'dataPrediction',
    titleKey: 'projectDialogs.dataPrediction.title',
    descriptionKey: 'projectDialogs.dataPrediction.description',
    thumbnail: dataPredictionModel,
    tech: ['React', 'TypeScript', 'CSS', 'Material UI'],
    githubUrl: 'https://github.com/SirArchy/ProjektMFG',
    DialogComponent: DataPredictionDialog,
  },
  {
    id: 'websiteRemake',
    titleKey: 'projectDialogs.websiteRemake.title',
    descriptionKey: 'projectDialogs.websiteRemake.description',
    thumbnail: websiteRemake,
    tech: ['React', 'TypeScript', 'CSS', 'Spline', 'Firebase'],
    githubUrl: 'https://github.com/SirArchy/PersonalWebsiteRemake',
    DialogComponent: WebsiteRemakeDialog,
  },
  {
    id: 'workTimeTracker',
    titleKey: 'projectDialogs.workTimeTracker.title',
    descriptionKey: 'projectDialogs.workTimeTracker.description',
    thumbnail: workTimeTrackerImage1,
    tech: ['Flutter', 'Dart', 'Material UI', 'Firebase', 'Android'],
    googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.yourdomain.worktimetracker',
    DialogComponent: WorkTimeTrackerDialog,
  },
  {
    id: 'zenRadar',
    titleKey: 'projectDialogs.zenRadar.title',
    descriptionKey: 'projectDialogs.zenRadar.description',
    thumbnail: zenRadarImage1,
    tech: ['Flutter', 'Dart', 'Material UI', 'SQLite', 'Android'],
    githubUrl: 'https://github.com/SirArchy/ZenRadar',
    googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.yourdomain.zenradar',
    DialogComponent: ZenRadarDialog,
  },
  {
    id: 'kitchenSync',
    titleKey: 'projectDialogs.kitchenSync.title',
    descriptionKey: 'projectDialogs.kitchenSync.description',
    thumbnail: kitchenSyncImage,
    tech: ['Flutter', 'Dart', 'Material UI', 'Firebase', 'Android'],
    websiteUrl: 'https://kitchensync-69c04.web.app',
    googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.yourdomain.kitchensync',
    DialogComponent: KitchenSyncDialog,
  },
  {
    id: 'coupleQuests',
    titleKey: 'projectDialogs.CoupleQuests.title',
    descriptionKey: 'projectDialogs.CoupleQuests.description',
    thumbnail: coupleQuestsImage,
    tech: ['Flutter', 'Dart', 'Material UI', 'Firebase', 'Android'],
    featureKeys: [
      'projectDialogs.CoupleQuests.feature1',
      'projectDialogs.CoupleQuests.feature2',
      'projectDialogs.CoupleQuests.feature3',
    ],
    websiteUrl: 'https://CoupleQuests-69c04.web.app',
    googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.yourdomain.CoupleQuests',
    DialogComponent: CoupleQuestsDialog,
  },
  {
    id: 'billKeeper',
    titleKey: 'projectDialogs.billKeeper.title',
    descriptionKey: 'projectDialogs.billKeeper.description',
    thumbnail: billKeeperImage,
    tech: ['Flutter', 'Offline-first', 'Android'],
    featureKeys: [
      'projectDialogs.billKeeper.feature1',
      'projectDialogs.billKeeper.feature2',
      'projectDialogs.billKeeper.feature3',
      'projectDialogs.billKeeper.feature4',
      'projectDialogs.billKeeper.feature5',
    ],
    DialogComponent: BillKeeperDialog,
  },
];
